import * as React from "react";
import {
  CardHeader,
  Divider,
  Stack,
  TextField,
  Typography,
  DialogActions,
  Dialog,
  DialogContent,
  Grid,
  DialogTitle,
  IconButton,
  InputLabel,
  MenuItem,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { RequestSoftwareForm } from "./RequestSoftware.types";
import {
  useMachineSummary,
  useAllowedSoftwareUpgrade,
  useSoftwareUpgradeRequest,
  useLatestSoftware
} from "services/machines.service";
import { Controller } from "react-hook-form";
import useForm from "hooks/useForm";
import { useTranslation } from "react-i18next";
import DashedDivider from "common/ui/DashedDivider";
import { useQueryClient } from "@tanstack/react-query";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { hasPinExpired, zeroPad } from "utils/common";
import _ from "lodash";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { setRequestSoftware } from "context/modal.context";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { selectRequestSoftwareProps } from "context/modal.selector";
import LoadingButton from "common/ui/LoadingButton";

enum SoftwarePackageCategory {
  Software = "1",
  Windows = "2",
  Language = "3"
}

const defaultValues = {
  software: 0,
  language: 0,
  windows: 0
};

export default function RequestSoftware() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [confirmSoftware, setConfirmSoftware] = React.useState(false);

  const { handleSubmit, control, reset, formState, watch } =
    useForm<RequestSoftwareForm>({
      defaultValues
    });

  const [hasDisabled, setHasDisabled] = React.useState({
    software: false,
    language: false,
    windows: false
  });

  const props = useAppSelector(selectRequestSoftwareProps);

  const machineDetails = useMachineSummary(props.machineId);
  const latestSoftware = useLatestSoftware(props.machineId);
  const allowedSoftware = useAllowedSoftwareUpgrade(props.machineId);
  const upgradeSoftware = useSoftwareUpgradeRequest(onSoftwareUpgrade);

  React.useEffect(() => {
    if (allowedSoftware.isError) {
      dispatch(setRequestSoftware({ open: false, machineId: null }));
    }
  }, [dispatch, allowedSoftware.isError]);

  React.useEffect(() => {
    if (props.open && latestSoftware.data) {
      const softwareDetails = {};
      latestSoftware.data.forEach((software) => {
        softwareDetails[software.softwarePackageCategoryId] = software;
      });

      const software = softwareDetails[SoftwarePackageCategory.Software]?.id;
      const windows = softwareDetails[SoftwarePackageCategory.Windows]?.id;
      const language = softwareDetails[SoftwarePackageCategory.Language]?.id;
      setHasDisabled({
        software: !!software,
        language: !!language,
        windows: !!windows
      });

      reset({
        software: software ?? 0,
        language: language ?? 0,
        windows: windows ?? 0
      });
    }
  }, [props.open, latestSoftware.data, reset]);

  React.useEffect(() => {
    if (formState.isSubmitSuccessful && !upgradeSoftware.isLoading) {
      reset(defaultValues);
    }
  }, [formState, upgradeSoftware.isLoading, reset]);

  function dismiss() {
    dispatch(setRequestSoftware({ open: false, machineId: null }));
    setConfirmSoftware(false);
  }

  function onModalDismiss() {
    reset(defaultValues);
    setHasDisabled({ software: false, windows: false, language: false });
    dismiss();
  }

  if (machineDetails.isLoading || allowedSoftware.isLoading) {
    return null;
  }

  const selectedSoftwareId = watch("software");
  const selectedWindowId = watch("windows");
  const selectedLanguageId = watch("language");

  const selectedSoftware = allowedSoftware.data?.softwares?.find(
    (item) => item.id === selectedSoftwareId
  );

  const selectedWindow = allowedSoftware.data?.windows?.find(
    (item) => item.id === selectedWindowId
  );

  const selectedLanguage = allowedSoftware.data?.languages?.find(
    (item) => item.id === selectedLanguageId
  );

  const isPinExpired = hasPinExpired(
    machineDetails.data.machineSummary.serviceRecordPin.serviceRecordPinExpiry
  );

  async function onSoftwareUpgrade(_data, error) {
    await queryClient.invalidateQueries({
      queryKey: ["machine", "detail"],
      exact: false
    });

    await queryClient.invalidateQueries({
      queryKey: ["latest-software"],
      exact: false
    });

    if (!error) {
      toast.success(t("requestSoftware.softwareRequestSuccessful"));
    }

    if (location.pathname !== `/machines/${props.machineId}`) {
      navigate(`/machines/${props.machineId}`);
    }
    reset(defaultValues);
    dismiss();
  }

  function handleConfirmDismiss() {
    setConfirmSoftware(false);
  }

  function onSubmit(data: RequestSoftwareForm) {
    const serviceRecordId =
      machineDetails.data?.machineSummary.serviceRecordPin.serviceRecordId;

    if (!isPinExpired) {
      upgradeSoftware.mutate({
        serviceRecordId: serviceRecordId,
        machineSoftwareId: data.software,
        languagePackId: data.language,
        windowsUpdateId: data.windows
      });
    } else {
      if (location.pathname !== `/machines/${props.machineId}`) {
        navigate(`/machines/${props.machineId}`);
      }
      dismiss();
    }
  }

  const anyFieldHasValue =
    hasDisabled.windows || hasDisabled.software || hasDisabled.language;

  return (
    <Dialog open={props.open} scroll="body" maxWidth="sm" fullWidth>
      <DialogTitle
        component={CardHeader}
        color="primary"
        avatar={<SettingsIcon />}
        title={t("requestSoftware.requestSoftwareTitle")}
        titleTypographyProps={{ variant: "h6" }}
        action={
          <IconButton onClick={onModalDismiss}>
            <CloseIcon />
          </IconButton>
        }
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ py: 0 }}>
          <DashedDivider />
          <Stack direction="row" spacing={1} my={1}>
            <Typography variant="h6">
              {t("requestSoftware.theServicePinIs")}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {zeroPad(
                machineDetails.data?.machineSummary.serviceRecordPin.pin,
                6
              )}
            </Typography>
            {isPinExpired ? (
              <Stack direction="row" alignItems="center">
                <ErrorOutlineIcon sx={{ color: "error.main" }} />
                <Typography
                  variant="h6"
                  sx={{ color: "error.main" }}
                  fontWeight="medium"
                  ml={1}
                >
                  {t("requestSoftware.pinExpired")}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
          <DashedDivider />
          <Grid container sx={{ color: "grey", my: 2, px: 3 }}>
            <Grid xs={6} item container justifyContent="space-between">
              <Typography
                variant="body1"
                fontWeight="medium"
                alignSelf="center"
                noWrap
              >
                {t("requestSoftware.machineSerialNumber")}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ borderColor: "gray.dark" }}
              />
            </Grid>
            <Grid xs={6} item>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                ml={5}
                title={machineDetails.data?.machineSummary.name}
                noWrap
              >
                {_.defaultTo(machineDetails.data?.machineSummary.name, "-")}
              </Typography>
            </Grid>
            <Grid xs={6} item container justifyContent="space-between">
              <Typography
                variant="body1"
                fontWeight="medium"
                alignSelf="center"
                noWrap
              >
                {t("requestSoftware.machineNameOrNumber")}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ borderColor: "gray.dark" }}
              />
            </Grid>
            <Grid xs={6} item>
              <Typography variant="subtitle1" fontWeight="bold" ml={5} noWrap>
                {_.defaultTo(
                  machineDetails.data?.machineSummary.serialNumber,
                  "-"
                )}
              </Typography>
            </Grid>
          </Grid>

          {confirmSoftware ? (
            <>
              <DashedDivider />
              <Typography mt={2} mb={1} variant="subtitle1" fontWeight="medium">
                {t("requestSoftware.confirmMessage")}
              </Typography>
              <Grid container sx={{ color: "grey", my: 2, px: 3 }}>
                <Grid xs={6} item container justifyContent="space-between">
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    alignSelf="center"
                    noWrap
                  >
                    {t("requestSoftware.windows")}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{ borderColor: "gray.dark" }}
                  />
                </Grid>
                <Grid xs={6} item>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    ml={5}
                    noWrap
                    title={selectedWindow?.name}
                  >
                    {_.defaultTo(selectedWindow?.name, "-")}
                  </Typography>
                </Grid>
                <Grid xs={6} item container justifyContent="space-between">
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    alignSelf="center"
                    noWrap
                  >
                    {t("requestSoftware.software")}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{ borderColor: "gray.dark" }}
                  />
                </Grid>
                <Grid xs={6} item>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    ml={5}
                    noWrap
                    title={selectedSoftware?.name}
                  >
                    {_.defaultTo(selectedSoftware?.name, "-")}
                  </Typography>
                </Grid>
                <Grid xs={6} item container justifyContent="space-between">
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    alignSelf="center"
                    noWrap
                  >
                    {t("requestSoftware.language")}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{ borderColor: "gray.dark" }}
                  />
                </Grid>
                <Grid xs={6} item>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    ml={5}
                    noWrap
                    title={selectedLanguage?.name}
                  >
                    {_.defaultTo(selectedLanguage?.name, "-")}
                  </Typography>
                </Grid>
              </Grid>
            </>
          ) : !isPinExpired || anyFieldHasValue ? (
            <div>
              <DashedDivider />
              <Typography variant="subtitle1" sx={{ color: "black", mt: 2 }}>
                {t("requestSoftware.selectRequiredSoftwareUpgrades")}
              </Typography>
              <InputLabel htmlFor="windows">
                {t("requestSoftware.windows")}
              </InputLabel>
              <Controller
                control={control}
                name="windows"
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{ id: "windows" }}
                    select
                    fullWidth
                    disabled={hasDisabled.windows || isPinExpired}
                    size="small"
                    name="windows"
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: { sx: { maxHeight: 300 } }
                    }}
                  >
                    <MenuItem value={0}>{t("common.select")}</MenuItem>
                    {allowedSoftware.data?.windows?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <InputLabel htmlFor="software">
                {t("requestSoftware.software")}
              </InputLabel>
              <Controller
                control={control}
                name="software"
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{ id: "software" }}
                    disabled={hasDisabled.software || isPinExpired}
                    size="small"
                    select
                    fullWidth
                    name="software"
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: { sx: { maxHeight: 300 } }
                    }}
                  >
                    <MenuItem value={0}>{t("common.select")}</MenuItem>
                    {allowedSoftware.data?.softwares?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <InputLabel htmlFor="language">
                {t("requestSoftware.language")}
              </InputLabel>
              <Controller
                control={control}
                name="language"
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    inputProps={{
                      id: "language"
                    }}
                    disabled={hasDisabled.language || isPinExpired}
                    select
                    fullWidth
                    name="language"
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: { sx: { maxHeight: 300 } }
                    }}
                  >
                    <MenuItem value={0}>{t("common.select")}</MenuItem>
                    {allowedSoftware.data?.languages?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </div>
          ) : null}
        </DialogContent>
        <DashedDivider style={{ width: "92%", paddingTop: 10 }} />
        <DialogActions sx={{ justifyContent: "center", py: 2 }}>
          {!confirmSoftware ? (
            <Button
              variant="outlined"
              disabled={!formState.isDirty && !isPinExpired}
              onClick={() => setConfirmSoftware(true)}
            >
              {t("requestSoftware.finishAndViewMachine")}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleConfirmDismiss}
                color="gray"
              >
                {t("common.cancel")}
              </Button>
              <LoadingButton
                loading={upgradeSoftware.isLoading}
                type="submit"
                variant="outlined"
              >
                {t("common.confirm")}
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
